import { del, get, post, put } from '@api/api-client';
import {
  appendPaginationQuery,
  type PaginatedResponse,
  type PaginationParams,
} from '@api/pagination';

export type EditionStatus = 'open' | 'paused' | 'closed';

export interface EditionResponse {
  id: string;
  externalId: number | null;
  countryId: string;
  siteId: string;
  siteCode: string;
  /** 'sso' for a marketplace edition, 'password' for a PMS one. */
  authMode: 'sso' | 'password';
  /** The PMS product for a password edition (e.g. 'tuotempo'); null otherwise. */
  pmsType: string | null;
  name: string;
  year: number;
  status: EditionStatus;
  createdAt: string;
  updatedAt: string;
  country: string;
  countryCode: string;
  hasWinners: boolean;
}

interface CreateEditionDTO {
  countryId: string;
  /** A PMS site to own the edition; omitted for a marketplace (SSO) edition. */
  siteId?: string;
  name: string;
  year: number;
  externalId?: number;
}

interface UpdateEditionDTO {
  name?: string;
  year?: number;
  countryId?: string;
  /** Move the edition to another site; only accepted while it has no data. */
  siteId?: string;
  externalId?: number | null;
  status?: EditionStatus;
  clearWinners?: boolean;
}

/** Fetches editions with pagination. */
export function getEditions(
  params?: PaginationParams,
): Promise<PaginatedResponse<EditionResponse>> {
  return get<PaginatedResponse<EditionResponse>>(appendPaginationQuery('/editions', params));
}

/** Fetches a single edition by ID. */
export function getEditionById(id: string): Promise<EditionResponse> {
  return get<EditionResponse>(`/editions/${id}`);
}

/** Creates a new edition. */
export function createEdition(data: CreateEditionDTO): Promise<EditionResponse> {
  return post<EditionResponse>('/editions', data);
}

/** Updates an existing edition by ID. */
export function updateEdition(id: string, data: UpdateEditionDTO): Promise<EditionResponse> {
  return put<EditionResponse>(`/editions/${id}`, data);
}

/** Deletes an edition by ID. */
export function deleteEdition(id: string): Promise<void> {
  return del(`/editions/${id}`);
}

export interface ImportEditionRow {
  editionExternalId: number;
  countryCode: string;
  /** Site the edition belongs to. Omitted, the country's SSO site is used. */
  siteCode?: string;
  editionName: string;
  year: number;
  categoryExternalId: number;
  /** Nominee key of an SSO site. */
  dpDoctorId?: number;
  /** Nominee key of a PMS site: the HubSpot contact id. */
  hsid?: string;
  participantName: string;
  participantSurname?: string;
  photoUrl?: string;
  profileUrl?: string;
  city?: string;
}

export interface FailedImportRow {
  row: ImportEditionRow;
  reason: string;
}

export interface ImportEditionResult {
  editionsCreated: number;
  editionsUpdated: number;
  participantsCreated: number;
  participantsUpdated: number;
  categoryDetailsCreated: number;
  categoriesAssigned: number;
  nomineesAssigned: number;
  nomineesRemoved: number;
  errors: string[];
  failedRows: FailedImportRow[];
}

/** Imports edition: creates/updates edition, upserts participants, assigns categories and nominees. */
export function importEdition(rows: ImportEditionRow[]): Promise<ImportEditionResult> {
  return post<ImportEditionResult>('/editions/import', { rows });
}

export interface ImportWinnerRow {
  categoryExternalId: number;
  /**
   * The winner's identity. An SSO edition uses dpDoctorId, a PMS one hsid. Which
   * is required is decided server-side from the edition's site, so a wrong key
   * comes back as a failed row with a reason rather than being dropped here.
   */
  dpDoctorId?: number;
  hsid?: string;
  position: number;
  participantName?: string;
}

export interface FailedWinnerRow {
  row: ImportWinnerRow;
  reason: string;
}

export interface ImportWinnersResult {
  winnersMarked: number;
  editionClosed: boolean;
  errors: string[];
  failedRows: FailedWinnerRow[];
}

/** Imports winners for an edition (marks ranking + isWinner) and closes the edition. */
export function importWinners(
  editionId: string,
  rows: ImportWinnerRow[],
): Promise<ImportWinnersResult> {
  return post<ImportWinnersResult>(`/editions/${editionId}/winners`, { rows });
}
